/* Grubwick backend client for the authenticated caller's role + permissions.
 *
 * GET /gw/me is the single source of truth the UI gates on (see AuthProvider):
 * fetched once after auth resolves, it says whether the caller is a regular
 * user, an owner (of specific restaurants), or an admin. Raw shape lives in
 * src/api/types.ts (ApiMe); this maps it onto the UI Me type. */
import { gwFetch, GwApiError, apiErrorMessage } from "./client";
import type { ApiMe } from "./types";

export type Role = "user" | "owner" | "admin";

/* The caller's identity + authorization, mapped for the UI. `ownedRestaurantIds`
   are UI restaurant ids (strings, matching Restaurant.id) so gating can compare
   directly against a restaurant in state. Empty for admins and regular users. */
export interface Me {
  id: string;
  email: string | null;
  role: Role;
  ownedRestaurantIds: string[];
}

export function mapApiMe(api: ApiMe): Me {
  return {
    id: api.id,
    email: api.email ?? null,
    role: api.role,
    ownedRestaurantIds: (api.owned_restaurant_ids ?? []).map(String),
  };
}

export async function fetchMe(): Promise<Me> {
  const res = await gwFetch("/me");
  if (!res.ok) {
    throw new GwApiError(
      res.status,
      apiErrorMessage(res.status, `Failed to load account (${res.status})`),
    );
  }
  const data: ApiMe = await res.json();
  return mapApiMe(data);
}
